/**
 * OpenAI Realtime server events, read into what the voice seam cares about.
 *
 * Pure, so the protocol can be tested with recorded events and no WebRTC.
 * Shapes per https://developers.openai.com/api/docs/guides/realtime-conversations
 * and the server events reference. Anything not listed is ignored.
 */

export type RealtimeRead =
  | { kind: 'transcript'; speaker: 'person' | 'voice'; text: string }
  | { kind: 'tool_call'; callId: string; name: string; args: unknown }
  | { kind: 'speech_started' }
  | { kind: 'speech_stopped' }
  | { kind: 'response_started' }
  | { kind: 'response_done' }
  | { kind: 'error'; message: string }
  | { kind: 'ignored' }

const str = (x: unknown): string => (typeof x === 'string' ? x : '')

function parseArgs(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw ?? {}
  try {
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

/**
 * One server event, read. `response.done` can carry function calls the
 * `function_call_arguments.done` event already reported; the caller dedupes
 * on `callId`.
 */
export function readRealtimeEvent(event: unknown): RealtimeRead[] {
  const e = (event ?? {}) as Record<string, unknown>
  switch (e.type) {
    case 'conversation.item.input_audio_transcription.completed': {
      const text = str(e.transcript).trim()
      return text ? [{ kind: 'transcript', speaker: 'person', text }] : []
    }
    case 'response.output_audio_transcript.done':
    case 'response.audio_transcript.done': {
      const text = str(e.transcript).trim()
      return text ? [{ kind: 'transcript', speaker: 'voice', text }] : []
    }
    case 'response.function_call_arguments.done':
      return str(e.call_id) && str(e.name)
        ? [
            {
              kind: 'tool_call',
              callId: str(e.call_id),
              name: str(e.name),
              args: parseArgs(e.arguments),
            },
          ]
        : []
    case 'response.created':
      return [{ kind: 'response_started' }]
    case 'response.done': {
      const response = (e.response ?? {}) as { output?: unknown }
      const output = Array.isArray(response.output) ? response.output : []
      const calls: RealtimeRead[] = []
      for (const item of output as Array<Record<string, unknown>>) {
        if (item?.type === 'function_call' && str(item.call_id))
          calls.push({
            kind: 'tool_call',
            callId: str(item.call_id),
            name: str(item.name),
            args: parseArgs(item.arguments),
          })
      }
      return [...calls, { kind: 'response_done' }]
    }
    case 'input_audio_buffer.speech_started':
      return [{ kind: 'speech_started' }]
    case 'input_audio_buffer.speech_stopped':
      return [{ kind: 'speech_stopped' }]
    case 'error': {
      const err = (e.error ?? {}) as { message?: unknown }
      return [{ kind: 'error', message: str(err.message) || 'voice error' }]
    }
    default:
      return [{ kind: 'ignored' }]
  }
}

/** The client events the adapter sends, built here so tests can read them. */
export const realtimeClient = {
  sessionUpdate(opts: {
    instructions: string
    tools: Array<{
      name: string
      description: string
      parameters: Record<string, unknown>
    }>
    /** ISO-639-1. Pinned, so a short phrase is not detected as another language. */
    language?: string
    /**
     * Reply on its own each time the person stops talking. Off when the voice
     * only speaks for the agent (RFC 0307 3e): its own turn after every line
     * was where it answered things only the agent can.
     */
    respondToSpeech?: boolean
  }) {
    return {
      type: 'session.update',
      session: {
        type: 'realtime',
        instructions: opts.instructions,
        tools: opts.tools.map((t) => ({ type: 'function', ...t })),
        tool_choice: 'auto',
        audio: {
          input: {
            transcription: {
              model: 'gpt-4o-mini-transcribe',
              language: opts.language ?? 'en',
            },
            turn_detection: {
              type: 'semantic_vad',
              create_response: opts.respondToSpeech ?? true,
            },
          },
        },
      },
    }
  },
  toolResult(callId: string, output: unknown) {
    return {
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: typeof output === 'string' ? output : JSON.stringify(output),
      },
    }
  },
  /** Context the model reads, with no reply forced. */
  context(text: string) {
    return {
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'system',
        content: [{ type: 'input_text', text }],
      },
    }
  },
  respond() {
    return { type: 'response.create' }
  },
}
